import * as React from 'react';

import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  Typography
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useTranslation } from 'react-i18next';

import { blackText, blue, gray, primarycolor } from '../../constants/Colors';
import { formLabelWrapper, textMb, titleMb } from '../../constants/Location';

import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';

type radioProps = React.ComponentProps<typeof RadioGroup>;

type Props = radioProps & {
  labelText?: string;
  requiredText?: string;
  isRequired: boolean;
  error?: boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined;
  width?: string;
  trueOption?: string;
  falseOption?: string;
  disabled?: boolean;
  guideText?: string;
  disabledColor?: string;
};

const styles = {
  formLabelWrapper,
  guideCard: {
    '@media screen and (max-width:600px)': {
      width: '80%'
    },
    width: '480px'
  },
  guideCloseButton: {
    color: blue,
    fontSize: '12px',
    marginLeft: '24px'
  },
  guideMark: {
    color: gray,
    cursor: 'pointer',
    heihgt: '18px',
    marginLeft: '4px',
    verticalAlign: 'middle',
    width: '18px'
  },
  guideText: {
    color: blackText,
    fontSize: '14px',
    whiteSpace: 'pre-wrap'
  },
  guideTitle: {
    color: blackText,
    fontSize: '22px',
    marginBottom: titleMb,
    padding: 0
  },
  helper: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    },
    marginLeft: 0
  },
  label: {
    fontSize: '12px'
  },
  required: {
    color: primarycolor,
    fontSize: '12px',
    marginLeft: '10px'
  }
};

const CustomRedioButton = React.forwardRef(
  (props: Props): React.ReactElement => {
    const {
      labelText,
      requiredText,
      isRequired,
      error,
      helperText,
      trueOption,
      falseOption,
      disabled,
      guideText,
      disabledColor,
      ...rest
    } = props;

    const { t } = useTranslation();
    const textfieldHelper = (): string | undefined => {
      if (helperText === undefined) return;
      return String(helperText);
    };

    const [open, setOpen] = React.useState(false);

    const handleClickOpen = (): void => {
      setOpen(true);
    };

    const handleClose = (): void => {
      setOpen(false);
    };

    return (
      <Typography sx={textMb} align="center" component="div">
        <Typography align="left" sx={styles.formLabelWrapper} component="div">
          <Typography color={gray} sx={styles.label} component="span">
            {labelText}
          </Typography>
          {isRequired ? (
            <Typography component="span" sx={styles.required}>
              {requiredText}
            </Typography>
          ) : (
            ''
          )}
          {guideText ? (
            <Typography component="span" sx={{ marginTop: '10px' }}>
              <HelpOutlineOutlinedIcon
                sx={styles.guideMark}
                onClick={handleClickOpen}
              />
              <Dialog open={open} onClose={handleClose}>
                <DialogContent sx={styles.guideCard}>
                  <DialogTitle sx={styles.guideTitle}>
                    {labelText}
                    {t('form.guideTitle')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {guideText}
                  </DialogContentText>
                  <DialogActions>
                    <Button onClick={handleClose} sx={styles.guideCloseButton}>
                      {t('form.guideClose')}
                    </Button>
                  </DialogActions>
                </DialogContent>
              </Dialog>
            </Typography>
          ) : (
            ''
          )}
        </Typography>
        <Typography align="left" component="div">
          <FormControl>
            <RadioGroup
              row
              aria-labelledby="demo-form-control-label-placement"
              name="position"
              sx={{
                '& .Mui-disabled': {
                  WebkitTextFillColor: disabledColor ?? 'initial'
                },
                '&.MuiFormGroup-root': { alignContent: 'flex-start' }
              }}
              {...rest}
            >
              <FormControlLabel
                value="true"
                control={<Radio sx={{ color: gray }} />}
                label={trueOption}
                sx={[
                  blackText,
                  {
                    '& .Mui-disabled': {
                      WebkitTextFillColor: disabledColor ?? 'initial'
                    },
                    '.MuiSvgIcon-root': {
                      color:
                        disabled && disabledColor ? disabledColor : 'inherit' // 任意の色
                    }
                  }
                ]}
                {...(disabled && { disabled })}
              />
              <FormControlLabel
                value="false"
                control={<Radio sx={{ color: gray }} />}
                label={falseOption}
                sx={[
                  blackText,
                  {
                    '& .Mui-disabled': {
                      WebkitTextFillColor: disabledColor ?? 'initial'
                    },
                    '.MuiSvgIcon-root': {
                      color:
                        disabled && disabledColor ? disabledColor : 'inherit' // 任意の色
                    }
                  }
                ]}
                {...(disabled && { disabled })}
              />
            </RadioGroup>
          </FormControl>
        </Typography>
        <FormHelperText sx={styles.helper} error={error}>
          {textfieldHelper()}
        </FormHelperText>
      </Typography>
    );
  }
);
CustomRedioButton.displayName = 'CustomRedioButton';
export default CustomRedioButton;
